import logger from '../../utils/logger-rum';
import React, { useEffect, useRef, useState } from 'react';

// import { useTypedSelector } from '../../store/reducers/use-typed-selector';
import { useSelector } from 'react-redux';
import { BrandliveEvent } from './working-model';
import TARGET_ORIGIN from './post-message-hooks';
import { GreenroomState as GS } from '../../stores/types/types';
import { CheckEventPublishedStatus } from '../../connection/meeting';
import ComingSoon from '../general-components/coming-soon';

// EXAMPLE USAGE (will NOT work in production):
// log into admin console
// open developer console and paste this and refresh page (replace the eventUuid, sessionUuid and language with your event):
/*
localStorage.setItem('CHAT_EMBED_TEST_DATA', JSON.stringify({
	"eventUuid": "cd9712620-53e1-11ee-9746-6fe4a4355ed13",
	"sessionUuid": "cd8afb0e-53e1-1124-9746-6fe4a671ed13",
	"language": "en"
}));
*/
// to remove it, just run this in te developer console and refresh the page:
/* localStorage.removeItem('CHAT_EMBED_TEST_DATA'); */

enum PublishedStatus {
	Deleted = 0,
	Published = 1,
	UnpublishedChanges = 2
}

interface EventStatus {
	url?: string;
	status: PublishedStatus;
}

const ModeratorChat = () => {
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const iframeCurrent = iframeRef.current;

	const [windowLoaded, setWindowLoaded] = useState(false);
	const [, setChatFeed] = useState<Map<number, unknown> | null>(null);
	const [, setEvent] = useState<BrandliveEvent | null>(null);
	const [, setRegistration] = useState<unknown>(null);
	const [, setModeratorRegistration] = useState<unknown>(null);
	const [, setUserSession] = useState<unknown>(null);

	const [eventData, setEventData] = useState<{ eventUuid: string; sessionUuid: string; language: string; } | null>(null);
	const [eventStatus, setEventStatus] = useState<EventStatus | null>(null);

	const token = useSelector((s: GS) => s.SessionReducer.events_token);
	const sessionUuid = useSelector((s: GS) => s.SessionReducer.meeting?.events_session_uuid);
	const eventUuid = useSelector((s: GS) => s.SessionReducer.meeting?.event_uuid);
	const language = useSelector((s: GS) => s.SessionReducer.meeting?.language);
	const isProd = process.env.REACT_APP_STAGE === 'prod';
	const iframeBaseURL = process.env.REACT_APP_PLATFORM_URL;

	const checkEventStatus = async (eventsToken: string | null, uuid: string) => {
		if (!eventsToken) return;
		const status = await CheckEventPublishedStatus(eventsToken, uuid);
		if (status) {
			setEventStatus(status);
		}
	};

	useEffect(() => {
		const storageTestingData = localStorage.getItem('CHAT_EMBED_TEST_DATA');
		if (sessionUuid && eventUuid && language) {
			const data = JSON.parse(storageTestingData || '{}');
			setEventData({
				eventUuid: eventUuid || data.eventUuid,
				sessionUuid: sessionUuid || data.sessionUuid,
				language: language || data.language,
			});
			checkEventStatus(token, eventUuid);
		} else {
			logger.error('ModeratorChat: missing sessionUuid, eventUuid, or language');
		}
	}, [isProd]);

	useEffect(() => {
		if (!eventData?.eventUuid || !eventData.sessionUuid || !eventData.language) return;

		const handleWindowLoad = () => {
			setWindowLoaded(true);
		};
		if (document.readyState === 'complete') {
			handleWindowLoad();
		} else {
			window.addEventListener('load', handleWindowLoad);
		}
		return () => { window.removeEventListener('load', handleWindowLoad); };
	}, [isProd, eventData]);

	const sendIframeData = () => {
		iframeCurrent?.contentWindow?.postMessage({
			payload: {
				blToken: token,
				style: `.embed-chat-feed {
						background: #26242b;
					}
					.embed-chat-feed-container {
						background: #26242b;
					}`,
			},
		}, TARGET_ORIGIN);
	};

	useEffect(() => {
		iframeCurrent?.addEventListener('load', sendIframeData, { once: true });
		return () => {
			iframeCurrent?.removeEventListener('load', sendIframeData);
		};
	}, [iframeCurrent]);

	useEffect(() => {
		if (isProd) return;
		if (!token || !windowLoaded) return;
		if (!eventData?.eventUuid || !eventData.sessionUuid || !eventData.language) return;

		const handleMessage = (e: MessageEvent) => {
			if (e?.data?.payload?.blRegistration) {
				setRegistration(e.data.payload.blRegistration);
			}
			if (e?.data?.payload?.blChatFeed) {
				setChatFeed(e.data.payload.blChatFeed);
			}
			if (e?.data?.payload?.blIncomingChatMessage) {
				const msg = e.data.payload.blIncomingChatMessage;
				// add to chat map
				setChatFeed((prev) => {
					const comments = new Map(prev);
					comments.set(msg.id, msg);
					return comments;
				});
			}
			if (e?.data?.payload?.blEvent) {
				setEvent(e.data.payload.blEvent);
			}
			if (e?.data?.payload?.blModeratorRegistration) {
				setModeratorRegistration(e.data.payload.blModeratorRegistration);
			}
			if (e?.data?.payload?.blUserSession) {
				setUserSession(e.data.payload.blUserSession);
			}
		};
	}, [token, windowLoaded, eventData, isProd]);

	// there is a tiny moment where the admin console page shows as the iframe is loading,
	// this can look like a white blank webpage, or a dark gray webpage with or without a
	// loading spinner (but in an older style)
	// - figure it is best to hide that and display blank bg color
	const [display, setDisplay] = useState<boolean>(false);

	if (
		isProd
		|| !eventData?.eventUuid
		|| !eventData.sessionUuid
		|| !eventData.language
		|| !iframeBaseURL
	) {
		logger.debug(`flag stopped chat rendering: 1.${isProd} 2.${!eventData?.eventUuid} 3.${!eventData?.sessionUuid} 4.${!eventData?.language} 5.${!iframeBaseURL}`);
		return null;
	}

	if (
		eventStatus
		&& eventStatus.status !== PublishedStatus.Published
	) {
		return (
			<ComingSoon
				title={'Page not published'}
				body={'Publish your session page to begin seeing the chat feed'}
			/>
		);
	}

	return (
		<iframe
			onLoad={() => setTimeout(() => setDisplay(true), 1)}
			style={{ opacity: display ? 1 : 0 }}
			ref={iframeRef}
			src={`${iframeBaseURL}/${eventData.eventUuid}/${eventData.sessionUuid}/${eventData.language}?embed=true`}
			onError={(e) => {
				logger.error('ModeratorChat iframe error', e);
			}}
		/>
	);
};

export default ModeratorChat;
